import { useState } from "react";
import { motion } from "framer-motion";
import { Reveal, Eyebrow, EASE } from "@/components/primitives";

const STRENGTHS = [
  {
    title: "The Architect–MEP Co-Founder Model",
    desc: "Your architect and MEP engineer are not vendors; they founded this company together.",
    img: "https://images.unsplash.com/photo-1758448656987-cfae6bf225e4?q=80&w=1200&auto=format&fit=crop",
  },
  {
    title: "50+ Years Industry Experience",
    desc: "ARA Designs' international portfolio + Turnkey's 40+ electrical legacy, under one contract.",
    img: "https://images.pexels.com/photos/164586/pexels-photo-164586.jpeg?auto=compress&cs=tinysrgb&w=1200",
  },
  {
    title: "End-to-End Technical Expertise",
    desc: "Design, civil, interiors, electrical, HVAC, plumbing, networking; every discipline, one team.",
    img: "https://images.pexels.com/photos/36713442/pexels-photo-36713442.jpeg?auto=compress&cs=tinysrgb&w=1200",
  },
  {
    title: "Curated Best-in-Class Vendor Network",
    desc: "No factory lock-in. We select the right specialist for every brief; always.",
    img: "https://images.pexels.com/photos/15227142/pexels-photo-15227142.jpeg?auto=compress&cs=tinysrgb&w=1200",
  },
];

export default function Strengths() {
  const [open, setOpen] = useState(0);

  return (
    <section className="bg-paper px-6 pb-24 pt-4 lg:px-12 lg:pb-36" data-testid="strengths-section">
      <div className="mx-auto max-w-[1440px]">
        <Reveal>
          <Eyebrow index="06">Why ARA Turnkey</Eyebrow>
        </Reveal>
        <Reveal delay={0.1}>
          <h2 className="mt-6 font-serif text-4xl leading-tight text-ink sm:text-5xl">
            Where architecture <em className="italic text-maroon">meets</em> engineering
          </h2>
        </Reveal>
        <Reveal delay={0.2}>
          <p className="mt-6 max-w-xl text-base leading-relaxed text-ink/65">
            One team, 50+ years of expertise, and always the best specialist for your project.
          </p>
        </Reveal>

        <div className="mt-14 flex flex-col gap-3 lg:h-[560px] lg:flex-row" data-testid="strengths-panels">
          {STRENGTHS.map((s, i) => (
            <motion.div
              key={s.title}
              initial={{ opacity: 0, y: 28 }}
              whileInView={{ opacity: 1, y: 0, transition: { duration: 0.8, delay: i * 0.07, ease: EASE } }}
              viewport={{ once: true, amount: 0.15 }}
              onMouseEnter={() => setOpen(i)}
              onClick={() => setOpen(i)}
              animate={{ flexGrow: open === i ? 2.4 : 1, transition: { duration: 0.65, ease: EASE } }}
              className="group relative min-h-[120px] cursor-pointer overflow-hidden rounded-2xl bg-ink lg:min-h-0"
              style={{ flexBasis: 0 }}
              data-testid={`strength-panel-${i + 1}`}
            >
              <img
                src={s.img}
                alt=""
                loading="lazy"
                className="absolute inset-0 h-full w-full object-cover"
                style={{ opacity: open === i ? 0.7 : 0.35, transition: "opacity 0.7s ease" }}
              />
              <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/45 to-ink/10" />
              <div className="relative z-10 flex h-full flex-col p-6 lg:min-h-0 lg:justify-between lg:p-8">
                <span className="font-sans text-sm font-bold" style={{ color: "#C8554F" }}>S{i + 1}</span>
                <div className="flex min-h-0 flex-1 items-center overflow-hidden py-4">
                  {open === i ? (
                    <motion.h3
                      initial={{ opacity: 0, y: 12 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.5, delay: 0.15, ease: EASE }}
                      className="font-serif text-2xl leading-snug text-paper lg:text-3xl"
                      data-testid={`strength-title-${i + 1}`}
                    >
                      {s.title}
                    </motion.h3>
                  ) : (
                    <>
                      <h3
                        className="hidden font-serif text-xl leading-snug text-paper/85 lg:block xl:text-2xl"
                        style={{ writingMode: "vertical-rl", transform: "rotate(180deg)" }}
                      >
                        {s.title}
                      </h3>
                      <h3 className="font-serif text-xl leading-snug text-paper/85 lg:hidden">{s.title}</h3>
                    </>
                  )}
                </div>
                <div
                  className="grid transition-[grid-template-rows,opacity] duration-500 ease-out"
                  style={{ gridTemplateRows: open === i ? "1fr" : "0fr", opacity: open === i ? 1 : 0 }}
                  data-testid={`strength-desc-${i + 1}`}
                >
                  <div className="overflow-hidden">
                    <div className="flex flex-col gap-4 pt-6">
                      <span className="h-[3px] w-12" style={{ backgroundColor: "#C8554F" }} />
                      <p className="max-w-sm text-sm leading-relaxed text-paper/80">{s.desc}</p>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
